import React, { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiShare, FiX } from 'react-icons/fi';

const DISMISS_KEY = 'hris.pwa.install.dismissed';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

function detectIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ sering melapor sebagai Mac
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

/**
 * Banner instalasi PWA.
 * - Android/Chrome: tombol Install via beforeinstallprompt
 * - iPhone/iPad Safari: panduan Share → Tambah ke Layar Utama (Safari tidak punya tombol Install)
 */
const PwaInstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isStandaloneDisplay() || wasDismissedRecently()) return undefined;

    const ios = detectIos();
    setIsIos(ios);

    if (ios) {
      setVisible(true);
      return undefined;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', () => {
      setVisible(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-4 pointer-events-none"
      role="dialog"
      aria-label="Pasang aplikasi HRIS"
    >
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-charcoal-200 dark:border-charcoal-700 bg-white/95 dark:bg-charcoal-900/95 backdrop-blur-xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl overflow-hidden ring-1 ring-charcoal-200 dark:ring-charcoal-700 bg-charcoal-50">
            <img src="/logo192.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-charcoal-900 dark:text-white">
              Pasang Dewatanet HRIS
            </p>
            {isIos ? (
              <ol className="mt-2 space-y-1.5 text-xs text-charcoal-600 dark:text-charcoal-300 leading-relaxed">
                <li className="flex gap-2">
                  <span className="font-bold text-dewata-600">1.</span>
                  <span>
                    Ketuk tombol <FiShare className="inline -mt-0.5 text-dewata-600" aria-hidden />{' '}
                    <strong>Bagikan</strong> di Safari (bawah layar).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-dewata-600">2.</span>
                  <span>
                    Gulir lalu pilih <strong>Tambah ke Layar Utama</strong> / Add to Home Screen.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-dewata-600">3.</span>
                  <span>
                    Ketuk <strong>Tambah</strong>. Ikon HRIS akan muncul di Home Screen.
                  </span>
                </li>
              </ol>
            ) : (
              <p className="mt-1 text-xs text-charcoal-600 dark:text-charcoal-300 leading-relaxed">
                Pasang ke layar HP agar lebih cepat dibuka dan notifikasi lebih andal.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-charcoal-400 hover:text-charcoal-700 hover:bg-charcoal-100 dark:hover:bg-charcoal-800"
            aria-label="Tutup"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {!isIos && deferredPrompt && (
          <button
            type="button"
            onClick={handleInstall}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-dewata-600 hover:bg-dewata-700 text-white text-sm font-semibold py-2.5 px-4 transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Install aplikasi
          </button>
        )}

        {isIos && (
          <p className="mt-3 text-[11px] text-charcoal-400 dark:text-charcoal-500">
            Safari iPhone tidak menampilkan tombol Install — pasang lewat tombol Bagikan di bawah.
          </p>
        )}
      </div>
    </div>
  );
};

export default PwaInstallBanner;
